import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, Tag, Badge, Button, Select, message, Drawer, Descriptions, Divider, Typography, Space, Spin, Alert, Tooltip } from 'antd';
import { EditDocsSales } from "src/features/Modal";
import {
  ClockCircleOutlined, UserOutlined, LeftOutlined, RightOutlined,
  CalendarOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ShoppingCartOutlined, EnvironmentOutlined, TagOutlined, IdcardOutlined, TruckOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import "./DocsSalesCalendar.css";

const defaultStatusGroups = {
  received:   { title: 'Новый заказ',      color: 'blue'     },
  processed:  { title: 'В обработке',      color: 'geekblue' },
  collecting: { title: 'Сборка начата',    color: 'orange'   },
  collected:  { title: 'Собран',           color: 'purple'   },
  picked:     { title: 'Передан курьеру',  color: 'cyan'     },
  delivered:  { title: 'Доставлен',        color: 'green'    },
};

const fullStatusGroups = {
  ...defaultStatusGroups,
  success: { title: 'Успешно завершен', color: 'green'   },
  closed:  { title: 'Закрыт',          color: 'magenta' },
};

const defaultStatusOrder = ['received','processed','collecting','collected','picked','delivered'];

const CDEK_GROUP_MAP = {
  CANCELLED:                                'CANCELLED',
  REMOVED:                                  'CANCELLED',
  CREATED:                                  'CREATED',
  ACCEPTED:                                 'ACCEPTED',
  RECEIVED_AT_SHIPMENT_WAREHOUSE:           'ACCEPTED',
  SENT_TO_SHIPMENT_WAREHOUSE:               'ACCEPTED',
  ACCEPTED_AT_SHIPMENT_WAREHOUSE:           'ACCEPTED',
  LOADED:                                   'ACCEPTED',
  READY_FOR_SHIPMENT:                       'ACCEPTED',
  READY_TO_SHIP_AT_SENDING_OFFICE:          'ACCEPTED',
  READY_FOR_SHIPMENT_IN_TRANSIT_CITY:       'ACCEPTED',
  PASSED_TO_CARRIER_AT_SENDING_OFFICE:      'ACCEPTED',
  READY_FOR_SHIPMENT_IN_SENDER_CITY:        'ACCEPTED',
  TAKEN_BY_TRANSPORTER_FROM_SENDER_CITY:    'ACCEPTED',
  SHIPPED:                                  'ACCEPTED',
  SEND_TO_TRANSIT_OFFICE:                   'IN_TRANSIT',
  SENT_TO_TRANSIT_CITY:                     'IN_TRANSIT',
  ACCEPTED_IN_TRANSIT_CITY:                 'IN_TRANSIT',
  TAKEN_BY_TRANSPORTER_FROM_TRANSIT_CITY:   'IN_TRANSIT',
  MET_AT_TRANSIT_OFFICE:                    'IN_TRANSIT',
  ACCEPTED_AT_TRANSIT_WAREHOUSE:            'IN_TRANSIT',
  READY_TO_SHIP_IN_TRANSIT_OFFICE:          'IN_TRANSIT',
  PASSED_TO_CARRIER_AT_TRANSIT_OFFICE:      'IN_TRANSIT',
  IN_TRANSIT:                               'IN_TRANSIT',
  IN_CARRIER_WAREHOUSE:                     'IN_TRANSIT',
  SENT_TO_RECIPIENT_CITY:                   'IN_TRANSIT',
  ACCEPTED_IN_RECIPIENT_CITY:               'IN_TRANSIT',
  ENTERED_TO_OFFICE_TRANSIT_WAREHOUSE:      'IN_TRANSIT',
  SHIPPED_TO_DESTINATION:                   'IN_TRANSIT',
  PASSED_TO_TRANSIT_CARRIER:                'IN_TRANSIT',
  IN_CUSTOMS_INTERNATIONAL:                 'IN_TRANSIT',
  IN_CUSTOMS_LOCAL:                         'IN_TRANSIT',
  CUSTOMS_COMPLETE:                         'IN_TRANSIT',
  ACCEPTED_AT_RECIPIENT_CITY_WAREHOUSE:     'AT_RECIPIENT',
  ACCEPTED_AT_PICK_UP_POINT:                'AT_RECIPIENT',
  DELIVERED_TO_RECIPIENT_WAREHOUSE:         'AT_RECIPIENT',
  POSTOMAT_POSTED:                          'AT_RECIPIENT',
  ENTERED_TO_DELIVERY_WAREHOUSE:            'AT_RECIPIENT',
  ENTERED_TO_WAREHOUSE_ON_DEMAND:           'AT_RECIPIENT',
  READY_FOR_RECIPIENT:                      'READY',
  TAKEN_BY_COURIER:                         'READY',
  DELIVERED:                                'DELIVERED',
  POSTOMAT_RECEIVED:                        'DELIVERED',
  NOT_DELIVERED:                            'NOT_DELIVERED',
  POSTOMAT_SEIZED:                          'NOT_DELIVERED',
  RETURNED_TO_SENDER_CITY_WAREHOUSE:        'NOT_DELIVERED',
  RETURNED_TO_TRANSIT_WAREHOUSE:            'NOT_DELIVERED',
  RETURNED_TO_RECIPIENT_CITY_WAREHOUSE:     'NOT_DELIVERED',
};

const cdekKanbanColumns = {
  CANCELLED:    { title: 'Отменён',               color: 'default'  },
  CREATED:      { title: 'Создан',                color: 'blue'     },
  ACCEPTED:     { title: 'Принят',                color: '#fa8c16'  },
  IN_TRANSIT:   { title: 'В пути',                color: 'gold'     },
  AT_RECIPIENT: { title: 'На складе получателя',  color: 'geekblue' },
  READY:        { title: 'Готов к выдаче',        color: 'green'    },
  DELIVERED:    { title: 'Вручен',                color: 'success'  },
  NOT_DELIVERED:{ title: 'Не вручен',             color: 'error'    },
};

const cdekKanbanOrder = ['CANCELLED','CREATED','ACCEPTED','IN_TRANSIT','AT_RECIPIENT','READY','DELIVERED','NOT_DELIVERED'];

const cdekStatusMap = {
  CREATED:                                  { color: 'blue',      text: 'Создан'               },
  ACCEPTED:                                 { color: 'cyan',      text: 'Принят'               },
  RECEIVED_AT_SHIPMENT_WAREHOUSE:           { color: '#fa8c16',   text: 'На складе отпр.'      },
  SENT_TO_SHIPMENT_WAREHOUSE:               { color: '#fa8c16',   text: 'Передан на склад'     },
  ACCEPTED_AT_SHIPMENT_WAREHOUSE:           { color: '#fa8c16',   text: 'Принят на склад'      },
  LOADED:                                   { color: '#fa8c16',   text: 'Загружен'             },
  READY_FOR_SHIPMENT:                       { color: '#fa8c16',   text: 'Готов к отправке'     },
  READY_TO_SHIP_AT_SENDING_OFFICE:          { color: '#fa8c16',   text: 'Выдан на отправку'    },
  READY_FOR_SHIPMENT_IN_TRANSIT_CITY:       { color: '#fa8c16',   text: 'Выдан на отправку'    },
  PASSED_TO_CARRIER_AT_SENDING_OFFICE:      { color: '#fa8c16',   text: 'Сдан перевозчику'     },
  SEND_TO_TRANSIT_OFFICE:                   { color: 'gold',      text: 'Отправлен в транзит'  },
  SENT_TO_TRANSIT_CITY:                     { color: 'gold',      text: 'Отправлен в транзит'  },
  MET_AT_TRANSIT_OFFICE:                    { color: 'gold',      text: 'В транзите'           },
  ACCEPTED_AT_TRANSIT_WAREHOUSE:            { color: 'gold',      text: 'На складе транзита'   },
  READY_TO_SHIP_IN_TRANSIT_OFFICE:          { color: '#fa8c16',   text: 'Выдан в транзите'     },
  PASSED_TO_CARRIER_AT_TRANSIT_OFFICE:      { color: '#fa8c16',   text: 'Сдан перевозчику'     },
  SHIPPED:                                  { color: 'gold',      text: 'Отправлен'            },
  IN_TRANSIT:                               { color: 'gold',      text: 'В пути'               },
  IN_CARRIER_WAREHOUSE:                     { color: 'gold',      text: 'На складе пер.'       },
  SENT_TO_RECIPIENT_CITY:                   { color: 'gold',      text: 'Отправлен получ.'     },
  ACCEPTED_IN_RECIPIENT_CITY:               { color: 'geekblue',  text: 'Встречен у получ.'    },
  ACCEPTED_AT_RECIPIENT_CITY_WAREHOUSE:     { color: 'geekblue',  text: 'На складе доставки'   },
  ACCEPTED_AT_PICK_UP_POINT:                { color: 'geekblue',  text: 'На складе до востр.'  },
  TAKEN_BY_COURIER:                         { color: 'purple',    text: 'Выдан на доставку'    },
  DELIVERED_TO_RECIPIENT_WAREHOUSE:         { color: 'green',     text: 'Доставлен в ПВЗ'      },
  READY_FOR_RECIPIENT:                      { color: 'green',     text: 'Готов к выдаче'       },
  POSTOMAT_POSTED:                          { color: 'green',     text: 'В постамате'          },
  DELIVERED:                                { color: 'success',   text: 'Вручен'               },
  POSTOMAT_RECEIVED:                        { color: 'success',   text: 'Изъят клиентом'       },
  NOT_DELIVERED:                            { color: 'error',     text: 'Не вручен'            },
  RETURNED_TO_SENDER_CITY_WAREHOUSE:        { color: 'default',   text: 'Возврат отправителю'  },
  RETURNED_TO_TRANSIT_WAREHOUSE:            { color: 'default',   text: 'Возврат на транзит'   },
  RETURNED_TO_RECIPIENT_CITY_WAREHOUSE:     { color: 'default',   text: 'Возврат на доставку'  },
  POSTOMAT_SEIZED:                          { color: 'red',       text: 'Изъят курьером'       },
  CANCELLED:                                { color: 'default',   text: 'Отменён'              },
  REMOVED:                                  { color: 'default',   text: 'Удалён'               },
};

const { Title } = Typography;

const isUnix10 = (n) => typeof n === 'number' && String(n).length === 10;

const formatDate = (val) => {
  if (val === null || val === undefined) return 'Не указано';
  if (isUnix10(val)) return moment.unix(val).format('DD.MM.YYYY HH:mm');
  if (moment.isMoment(val)) return val.format('DD.MM.YYYY HH:mm');
  return String(val);
};

const getCdekStatusKey = (order) => order.cdek_live_status || order.status || 'CREATED';
const getCdekGroupKey  = (order) => CDEK_GROUP_MAP[getCdekStatusKey(order)] || 'CREATED';

const prettyLabel = (key) => {
  const map = {
    id: 'ID', number: 'Номер', dated: 'Дата документа', operation: 'Операция',
    tags: 'Теги', docs_sales: 'Документ продажи', nomenclature_count: 'Позиции (шт.)',
    paid_doc: 'Оплачено (dok.)', paid_rubles: 'Оплачено (₽)', paid_loyality: 'Оплачено (баллы)',
    status: 'Активен', doc_discount: 'Скидка, %', comment: 'Комментарий', client: 'Клиент ID',
    contragent: 'Контрагент ID', contragent_name: 'Контрагент', contract: 'Договор ID',
    organization: 'Организация ID', warehouse: 'Склад ID', autorepeat: 'Автоповтор',
    settings: 'Настройки', sum: 'Сумма, ₽', tax_included: 'Цены с НДС', tax_active: 'НДС включён',
    sales_manager: 'Менеджер ID', goods: 'Товары', delivery_info: 'Доставка',
    updated_at: 'Обновлено', created_at: 'Создано', has_contragent: 'Есть контрагент',
    has_loyality_card: 'Есть карта лояльности', color_status: 'Цвет статуса',
    priority: 'Приоритет', order_status: 'Статус заказа',
    assigned_picker: 'Сборщик', assigned_courier: 'Курьер',
    picker_started_at: 'Начало сборки', picker_finished_at: 'Окончание сборки',
    courier_picked_at: 'Передан курьеру', courier_delivered_at: 'Доставлен',
    contragent_segments: 'Сегменты контрагента (ID)',
  };
  return map[key] || key;
};

const humanize = (key, val) => {
  const formatPerson = (a) => {
    if (a === null || a === undefined) return 'Не назначен';
    if (Array.isArray(a)) return a.length > 0 ? formatPerson(a[0]) : 'Не назначен';
    if (typeof a === 'object' && a.id && typeof a.id === 'object') return formatPerson(a.id);
    if (typeof a === 'string' || typeof a === 'number') return String(a);
    const first  = a.first_name ?? a.first ?? a.name ?? '';
    const second = a.last_name  ?? a.surname ?? '';
    const full   = [first, second].filter(Boolean).join(' ');
    if (full) return full;
    if (a.full_name) return a.full_name;
    if (a.id) return `ID: ${a.id}`;
    try { const s = JSON.stringify(a); return s.length > 120 ? s.slice(0, 120) + '…' : s; } catch { return String(a); }
  };
  if (['assigned_picker', 'assigned_courier'].includes(key)) return formatPerson(val);
  if (val === null || val === undefined) return 'Не указано';
  if (typeof val === 'boolean') return val ? 'Да' : 'Нет';
  if (['dated', 'updated_at', 'created_at'].includes(key)) return formatDate(val);
  if (key === 'nomenclature_count') return `${Number(val || 0)} шт.`;
  if (Array.isArray(val)) {
    if (val.length === 0) return 'Не указано';
    if (val.every(v => typeof v === 'number' || typeof v === 'string')) return val.join(', ');
    try { return JSON.stringify(val, null, 2); } catch { return String(val); }
  }
  if (key === 'sum' || key === 'paid_rubles') return `${Number(val || 0).toLocaleString('ru-RU')} ₽`;
  if (key === 'doc_discount') return `${val}%`;
  if (typeof val === 'object') { try { return JSON.stringify(val, null, 2); } catch { return String(val); } }
  if (isUnix10(val)) return formatDate(val);
  return String(val);
};

const renderFullPaid = (row) => {
  const paid = Number(row?.paid_doc || 0);
  const sum  = Number(row?.sum || 0);
  const disc = Number(row?.doc_discount || 0);
  return paid >= sum - disc
    ? <span style={{ color: 'green' }}><CheckCircleOutlined /> Оплачено</span>
    : <span style={{ color: 'red' }}><CloseCircleOutlined /> Не оплачено</span>;
};

const getOrderDateMoment = (order, mode = 'delivery') => {
  if (!order) return null;
  const val = mode === 'delivery'
    ? (order.delivery_info?.delivery_date ?? order.delivery_date ?? null)
    : (order.created_at ?? order.created ?? order.dated ?? null);
  if (!val) return null;
  if (isUnix10(val)) return moment.unix(val);
  if (moment.isMoment(val)) return val.clone();
  const p = moment(val);
  return p.isValid() ? p : null;
};

const SimpleCalendar = ({ value, onSelect }) => {
  const [viewMonth, setViewMonth] = useState(value.clone().startOf('month'));
  useEffect(() => setViewMonth(value.clone().startOf('month')), [value]);

  const start = viewMonth.clone().startOf('month').startOf('week');
  const weeks = [];
  let day = start.clone();
  for (let i = 0; i < 6; i++) {
    const days = [];
    for (let j = 0; j < 7; j++) { days.push(day.clone()); day.add(1, 'day'); }
    weeks.push(days);
  }
  const isSameDay = (a, b) => a && b && a.isSame(b, 'day');

  return (
    <div className="simple-calendar">
      <div className="sc-header">
        <Button size="small" onClick={() => setViewMonth(m => m.clone().subtract(1, 'month'))}>&lt;</Button>
        <div className="sc-month-label">{viewMonth.format('MMMM YYYY')}</div>
        <Button size="small" onClick={() => setViewMonth(m => m.clone().add(1, 'month'))}>&gt;</Button>
      </div>
      <div className="sc-grid">
        <div className="sc-weekdays">
          {moment.weekdaysShort().map(d => <div key={d} className="sc-weekday">{d}</div>)}
        </div>
        <div className="sc-weeks">
          {weeks.map((week, i) => (
            <div key={i} className="sc-week">
              {week.map(d => (
                <button
                  key={d.format('YYYY-MM-DD')}
                  className={[
                    'sc-day',
                    d.month() !== viewMonth.month() ? 'sc-day--muted'    : '',
                    isSameDay(d, moment())           ? 'sc-day--today'    : '',
                    isSameDay(d, value)              ? 'sc-day--selected' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => onSelect(d)}
                >
                  {d.date()}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const DocsSalesCalendar = ({
  dataSource,
  token,
  updateRow,
  tags,
  visibleStatuses  = [],
  cdekLiveStatuses = {},
  isCdekMode       = false,
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const [dateMode,          setDateMode]          = useState('delivery');
  const [detailsOpen,       setDetailsOpen]       = useState(false);
  const [selectedOrder,     setSelectedOrder]     = useState(null);
  const [loadingDetails,    setLoadingDetails]    = useState(false);
  const [errorDetails,      setErrorDetails]      = useState(null);
  const [loading,           setLoading]           = useState(false);
  const [error,             setError]             = useState(null);
  const [localData,         setLocalData]         = useState([]);
  const [currentDate,       setCurrentDate]       = useState(moment().startOf('day'));
  const [loadingStatus,     setLoadingStatus]     = useState({});
  const [datePickerVisible, setDatePickerVisible] = useState(false);

  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get('kanban') !== 'true') {
        p.set('kanban', 'true');
        window.history.replaceState(null, '', `${window.location.pathname}?${p.toString()}`);
      }
    } catch {}
  }, []);

  const fetchOrders = useCallback(async (date, mode) => {
    if (isCdekMode) return;
    setLoading(true); setError(null);
    try {
      const dateStr = date.format('YYYY-MM-DD');
      const url = mode === 'created'
        ? `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/created/${dateStr}/`
        : `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/delivery/${dateStr}/`;
      const res = await axios.get(url, { params: { token, limit: 1000 } });
      setLocalData(res.data.result || []);
    } catch (err) {
      setError(err);
      message.error('Не удалось загрузить заказы');
    } finally { setLoading(false); }
  }, [token, isCdekMode]);

  useEffect(() => {
    if (!isCdekMode) fetchOrders(currentDate, dateMode);
  }, [currentDate, dateMode, fetchOrders, isCdekMode]);

  useEffect(() => {
    if (dataSource !== undefined) {
      const arr = Array.isArray(dataSource) ? dataSource
        : (dataSource?.result && Array.isArray(dataSource.result)) ? dataSource.result : [];
      setLocalData(arr);
    }
  }, [dataSource]);

  const effectiveData = useMemo(() => {
    if (!cdekLiveStatuses || Object.keys(cdekLiveStatuses).length === 0) return localData;
    return localData.map(order => {
      if (!order.order_uuid) return order;
      const live = cdekLiveStatuses[order.order_uuid];
      return live ? { ...order, cdek_live_status: live } : order;
    });
  }, [localData, cdekLiveStatuses]);

  const cdekFilteredData = useMemo(() => {
    if (!isCdekMode) return effectiveData;
    return effectiveData.filter(order => {
      const dm = getOrderDateMoment(order, 'created');
      return dm && dm.isSame(currentDate, 'day');
    });
  }, [effectiveData, isCdekMode, currentDate]);

  const displayData = isCdekMode ? cdekFilteredData : effectiveData;

  const fetchOrderDetails = async (orderId) => {
    if (!orderId) return null;
    setLoadingDetails(true); setErrorDetails(null);
    try {
      const res = await axios.get(
        `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/${orderId}/`,
        { params: { token } }
      );
      return res.data;
    } catch (err) {
      setErrorDetails(err);
      message.error('Не удалось загрузить детали заказа');
      return null;
    } finally { setLoadingDetails(false); }
  };

  const openDetails = async (order) => {
    setSelectedOrder({ ...order });
    setDetailsOpen(true);
    if (!order.id) return;
    const full = await fetchOrderDetails(order.id);
    if (full) {
      const merged = {
        ...order, ...full,
        nomenclature_count: full.nomenclature_count ?? order.nomenclature_count,
        contragent_name:    full.contragent_name    ?? order.contragent_name,
        sum:                full.sum                ?? order.sum,
        dated:              full.dated              ?? order.dated,
        order_uuid:         order.order_uuid,
        status:             order.status,
        cdek_live_status:   order.cdek_live_status ?? full.cdek_live_status,
      };
      setSelectedOrder(merged);
      setLocalData(prev => prev.map(o => o.id === order.id ? { ...o, ...merged } : o));
    }
  };

  const closeDetails = () => { setDetailsOpen(false); setSelectedOrder(null); setErrorDetails(null); };

  const visibleStatusKeys = useMemo(() => {
    if (isCdekMode) return cdekKanbanOrder;
    const extras = (Array.isArray(visibleStatuses) ? visibleStatuses : [])
      .filter(s => ['success', 'closed'].includes(s));
    return [...defaultStatusOrder, ...extras.filter(s => !defaultStatusOrder.includes(s))];
  }, [visibleStatuses, isCdekMode]);

  const groupedOrders = useMemo(() => {
    if (isCdekMode) {
      const groups = {};
      displayData.forEach(order => {
        const key = getCdekGroupKey(order);
        if (!groups[key]) groups[key] = [];
        groups[key].push(order);
      });
      return groups;
    }
    const groups = {};
    effectiveData.forEach(order => {
      const dm = getOrderDateMoment(order, dateMode);
      if (!dm) return;
      const date = dm.format('YYYY-MM-DD');
      if (!groups[date]) groups[date] = {};
      const sg = (typeof order.order_status === 'string' && order.order_status) || 'received';
      if (!groups[date][sg]) groups[date][sg] = [];
      groups[date][sg].push(order);
    });
    return groups;
  }, [displayData, effectiveData, dateMode, isCdekMode]);

  const activeGroups = useMemo(() => {
    if (isCdekMode) return groupedOrders;
    return groupedOrders[currentDate.format('YYYY-MM-DD')] || {};
  }, [groupedOrders, currentDate, isCdekMode]);

  const statusSums = useMemo(() => {
    const sums = {};
    visibleStatusKeys.forEach(s => {
      sums[s] = (activeGroups[s] || []).reduce((acc, o) => acc + (Number(o.sum) || 0), 0);
    });
    return sums;
  }, [activeGroups, visibleStatusKeys]);

  const updateOrderStatusLocal = (orderId, newStatus) =>
    setLocalData(prev => prev.map(o => o.id === orderId ? { ...o, order_status: newStatus } : o));

  const handleStatusChange = async (orderId, newStatus) => {
    if (isCdekMode) { message.info('Статус СДЭК управляется автоматически'); return; }
    const order = localData.find(o => o.id === orderId);
    if (!order) { message.error('Заказ не найден'); return; }
    if (order.organization == null) { message.error('Не указана organization'); return; }
    const prevStatus = order.order_status || 'received';
    updateOrderStatusLocal(orderId, newStatus);
    setLoadingStatus(prev => ({ ...prev, [orderId]: true }));
    try {
      await axios.patch(
        `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/${orderId}/`,
        [{ id: orderId, organization: order.organization, order_status: newStatus }],
        { params: { token } }
      );
      const { data: full } = await axios.get(
        `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/${orderId}/`,
        { params: { token } }
      );
      setLocalData(prev => prev.map(o => o.id === orderId ? { ...o, ...full } : o));
      if (selectedOrder?.id === orderId) setSelectedOrder(prev => ({ ...(prev || {}), ...full }));
      message.success('Статус обновлен');
    } catch (err) {
      message.error('Ошибка обновления статуса — откат');
      updateOrderStatusLocal(orderId, prevStatus);
    } finally { setLoadingStatus(prev => ({ ...prev, [orderId]: false })); }
  };

  const handleDropToStatus = async (targetStatus, e) => {
    e.preventDefault();
    if (isCdekMode) { message.info('Статус СДЭК управляется автоматически'); return; }
    const idStr = e.dataTransfer.getData('text/plain');
    if (!idStr) return;
    const orderId = isNaN(Number(idStr)) ? idStr : Number(idStr);
    const order = localData.find(o => o.id === orderId);
    if (!order) return;
    const prevStatus = order.order_status || 'received';
    updateOrderStatusLocal(orderId, targetStatus);
    setLoadingStatus(prev => ({ ...prev, [orderId]: true }));
    try {
      await axios.patch(
        `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/${orderId}/status`,
        { status: targetStatus },
        { params: { token } }
      );
      const { data: full } = await axios.get(
        `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/${orderId}/`,
        { params: { token } }
      );
      setLocalData(prev => prev.map(o => o.id === orderId ? { ...o, ...full } : o));
      if (selectedOrder?.id === orderId) setSelectedOrder(prev => ({ ...(prev || {}), ...full }));
      message.success(`Статус: ${fullStatusGroups[targetStatus]?.title ?? targetStatus}`);
    } catch (err) {
      message.error('Ошибка обновления статуса — откат');
      updateOrderStatusLocal(orderId, prevStatus);
    } finally { setLoadingStatus(prev => ({ ...prev, [orderId]: false })); }
  };

  const sharedDateNav = (
    <div className="calendar-header">
      {isMobile ? (
        <>
          <div className="mobile-header-top">
            <Button icon={<LeftOutlined />} onClick={() => setCurrentDate(d => d.clone().subtract(1, 'days'))} />
            <Button type="primary" onClick={() => setCurrentDate(moment().startOf('day'))}>Сегодня</Button>
            <Button icon={<RightOutlined />} onClick={() => setCurrentDate(d => d.clone().add(1, 'days'))} />
          </div>
          <div className="mobile-header-bottom">
            <div className="current-date" onClick={() => setDatePickerVisible(v => !v)}>
              {currentDate.format('DD MMMM YYYY')}<CalendarOutlined />
            </div>
            {datePickerVisible && (
              <div className="date-picker-popover mobile">
                <SimpleCalendar value={currentDate} onSelect={d => { if (d) setCurrentDate(d.clone().startOf('day')); setDatePickerVisible(false); }} />
              </div>
            )}
            {!isCdekMode && (
              <Select value={dateMode} onChange={setDateMode} size="small">
                <Select.Option value="delivery">Доставка</Select.Option>
                <Select.Option value="created">Создание</Select.Option>
              </Select>
            )}
          </div>
        </>
      ) : (
        <>
          <Button icon={<LeftOutlined />} onClick={() => setCurrentDate(d => d.clone().subtract(1, 'days'))} />
          <div className="date-control">
            <Button type="primary" onClick={() => setCurrentDate(moment().startOf('day'))}>Сегодня</Button>
            <div className="current-date" onClick={() => setDatePickerVisible(v => !v)}>
              {currentDate.format('DD MMMM YYYY')}<CalendarOutlined />
            </div>
            {datePickerVisible && (
              <div className="date-picker-popover">
                <SimpleCalendar value={currentDate} onSelect={d => { if (d) setCurrentDate(d.clone().startOf('day')); setDatePickerVisible(false); }} />
              </div>
            )}
          </div>
          {!isCdekMode && (
            <Select value={dateMode} onChange={setDateMode} size="small">
              <Select.Option value="delivery">По дате доставки</Select.Option>
              <Select.Option value="created">По дате создания</Select.Option>
            </Select>
          )}
          <Button icon={<RightOutlined />} onClick={() => setCurrentDate(d => d.clone().add(1, 'days'))} />
        </>
      )}
    </div>
  );

  return (
    <div className="order-calendar">
      <Spin spinning={loading} tip="Загрузка заказов...">
        {error && (
          <Alert message="Ошибка загрузки" description={error.message} type="error" showIcon style={{ marginBottom: 16 }} />
        )}

        {sharedDateNav}

        <div className={`day-columns ${visibleStatusKeys.length > 6 ? 'allow-scroll' : ''} ${isMobile ? 'mobile-view' : ''}`}>
          {visibleStatusKeys.map(key => {
            const group = isCdekMode ? cdekKanbanColumns[key] : fullStatusGroups[key];
            if (!group) return null;
            const orders    = activeGroups[key] || [];
            const statusSum = statusSums[key] || 0;

            return (
              <div
                key={key}
                className="status-column"
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
                onDragLeave={e => e.currentTarget.classList.remove('drag-over')}
                onDrop={e => { e.currentTarget.classList.remove('drag-over'); handleDropToStatus(key, e); }}
              >
                <div className="column-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag color={group.color}>{group.title}</Tag>
                    <Badge count={orders.length} style={{ backgroundColor: group.color }} />
                  </div>
                  <div style={{ marginTop: 4, fontWeight: 500, fontSize: 14, color: statusSum > 0 ? '#1d39c4' : '#8c8c8c' }}>
                    {statusSum.toLocaleString('ru-RU')} ₽
                  </div>
                </div>
                <div className="orders-list">
                  {orders.map(order => (
                    <OrderCard
                      key={order.order_uuid || order.id}
                      order={order}
                      dateMode={dateMode}
                      isCdekMode={isCdekMode}
                      onStatusChange={handleStatusChange}
                      loading={loadingStatus[order.id]}
                      onOpenDetails={openDetails}
                      isMobile={isMobile}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <Drawer
          title={selectedOrder ? `Заказ #${selectedOrder.number || selectedOrder.id}` : 'Детали'}
          open={detailsOpen}
          onClose={closeDetails}
          width={window.innerWidth < 768 ? '100%' : 720}
          destroyOnClose
          extra={selectedOrder && (
            <Space>
              <EditDocsSales
                doc={selectedOrder}
                token={token}
                tags={tags}
                updateRow={(data, response) => {
                  if (typeof updateRow === 'function') {
                    try { updateRow(data, response); } catch {}
                    try {
                      const p = new URLSearchParams(window.location.search);
                      p.set('kanban', 'true');
                      window.history.replaceState(null, '', `${window.location.pathname}?${p.toString()}`);
                    } catch {}
                  }
                  const updated = response?.data ?? data;
                  if (updated?.id) {
                    setSelectedOrder(prev => ({ ...(prev || {}), ...updated }));
                    setLocalData(prev => prev.map(o => o.id === updated.id ? { ...o, ...updated } : o));
                  }
                }}
              />
              <Button onClick={() => { navigator.clipboard?.writeText(JSON.stringify(selectedOrder, null, 2)); message.success('Скопирован'); }}>
                JSON
              </Button>
            </Space>
          )}
        >
          {loadingDetails ? (
            <div style={{ textAlign: 'center', padding: 24 }}><Title level={4}>Загрузка...</Title></div>
          ) : errorDetails ? (
            <div style={{ textAlign: 'center', padding: 24, color: '#ff4d4f' }}>
              <Title level={4} style={{ color: '#ff4d4f' }}>Ошибка загрузки</Title>
              <p>{errorDetails.message}</p>
              <Button type="primary" onClick={() => openDetails(selectedOrder)} style={{ marginTop: 16 }}>Повторить</Button>
            </div>
          ) : selectedOrder ? (
            <div className="order-details-drawer">
              {(selectedOrder.cdek_live_status || selectedOrder.status) && (() => {
                const key  = selectedOrder.cdek_live_status || selectedOrder.status;
                const info = cdekStatusMap[key];
                return info ? (
                  <Alert
                    icon={<TruckOutlined />}
                    showIcon
                    type="info"
                    message={<span>СДЭК: <Tag color={info.color} style={{ margin: 0 }}>{info.text}</Tag></span>}
                    style={{ marginBottom: 12 }}
                  />
                ) : null;
              })()}

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: '#666' }}>Статус заказа:</div>
                <Select
                  value={selectedOrder.order_status || 'received'}
                  onChange={v => {
                    setSelectedOrder(prev => ({ ...(prev || {}), order_status: v }));
                    handleStatusChange(selectedOrder.id, v);
                  }}
                  size="small" style={{ minWidth: 240 }}
                  loading={Boolean(loadingStatus[selectedOrder.id])}
                  disabled={isCdekMode}
                >
                  {visibleStatusKeys.map(k => (
                    <Select.Option key={k} value={k}>
                      {isCdekMode ? (cdekKanbanColumns[k]?.title || k) : (fullStatusGroups[k]?.title || k)}
                    </Select.Option>
                  ))}
                </Select>
                <Tag color={fullStatusGroups[selectedOrder.order_status]?.color || 'default'}>
                  {fullStatusGroups[selectedOrder.order_status]?.title || selectedOrder.order_status}
                </Tag>
              </div>

              <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>Общая информация</Title>
              <Descriptions column={1} size="small" bordered>
                {['id','number','operation','contragent_name','sum','priority','color_status','order_status','nomenclature_count'].map(k => (
                  <Descriptions.Item key={k} label={prettyLabel(k)}>{humanize(k, selectedOrder[k])}</Descriptions.Item>
                ))}
              </Descriptions>
              <Divider />
              <Title level={5}>Платежи и налоги</Title>
              <Descriptions column={1} size="small" bordered>
                {['paid_doc','paid_rubles','paid_loyality','doc_discount','tax_included','tax_active','has_loyality_card'].map(k => (
                  <Descriptions.Item key={k} label={prettyLabel(k)}>{humanize(k, selectedOrder[k])}</Descriptions.Item>
                ))}
                <Descriptions.Item label="Оплачен полностью">{renderFullPaid(selectedOrder)}</Descriptions.Item>
              </Descriptions>
              <Divider />
              <Title level={5}>Связи / объекты</Title>
              <Descriptions column={1} size="small" bordered>
                {['client','contragent','contragent_segments','contract','organization','warehouse','sales_manager'].map(k => (
                  <Descriptions.Item key={k} label={prettyLabel(k)}>
                    {Array.isArray(selectedOrder[k]) ? selectedOrder[k].join(', ') : humanize(k, selectedOrder[k])}
                  </Descriptions.Item>
                ))}
              </Descriptions>
              <Divider />
              <Title level={5}>Время</Title>
              <Descriptions column={1} size="small" bordered>
                {['dated','created_at','updated_at'].map(k => (
                  <Descriptions.Item key={k} label={prettyLabel(k)}>{humanize(k, selectedOrder[k])}</Descriptions.Item>
                ))}
              </Descriptions>
              <Divider />
              <Title level={5}>Сборка и доставка</Title>
              <Descriptions column={1} size="small" bordered>
                {['assigned_picker','assigned_courier','picker_started_at','picker_finished_at','courier_picked_at','courier_delivered_at'].map(k => (
                  <Descriptions.Item key={k} label={prettyLabel(k)}>{humanize(k, selectedOrder[k])}</Descriptions.Item>
                ))}
              </Descriptions>
              <Divider />
              <Title level={5}>Доставка</Title>
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Адрес">{selectedOrder.delivery_info?.address || 'Не указано'}</Descriptions.Item>
                <Descriptions.Item label="Дата доставки">
                  {selectedOrder.delivery_info?.delivery_date ? formatDate(selectedOrder.delivery_info.delivery_date) : 'Не указано'}
                </Descriptions.Item>
                <Descriptions.Item label="Получатель">
                  {selectedOrder.delivery_info?.recipient ? (() => {
                    const r = selectedOrder.delivery_info.recipient;
                    return [[r?.surname, r?.name].filter(Boolean).join(' '), r?.phone].filter(Boolean).join(', ') || 'Не указано';
                  })() : 'Не указано'}
                </Descriptions.Item>
                <Descriptions.Item label="Примечание">{selectedOrder.delivery_info?.note || 'Не указано'}</Descriptions.Item>
              </Descriptions>
              <Divider />
              <Title level={5}>JSON</Title>
              <pre className="order-json-block">{JSON.stringify(selectedOrder, null, 2)}</pre>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 24 }}><Title level={4}>Заказ не найден</Title></div>
          )}
        </Drawer>
      </Spin>
    </div>
  );
};

const OrderCard = ({ order, dateMode = 'delivery', isCdekMode = false, onStatusChange, loading, onOpenDetails, isMobile }) => {
  const [isDragging, setIsDragging] = useState(false);

  const priorityMeta = (() => {
    const p = order.priority;
    if (p === null || p === undefined || p === '') return null;
    const n = Number(p);
    if (Number.isNaN(n)) return { label: String(p), color: 'default' };
    const v = Math.max(0, Math.min(10, Math.floor(n)));
    return { label: String(v), color: v >= 8 ? 'red' : v >= 5 ? 'orange' : 'default' };
  })();

  const cdekKey  = getCdekStatusKey(order);
  const cdekInfo = cdekKey ? cdekStatusMap[cdekKey] : null;

  const onDragStart = e => { e.dataTransfer.setData('text/plain', String(order.id)); e.dataTransfer.effectAllowed = 'move'; setIsDragging(true); };
  const onDragEnd   = () => setTimeout(() => setIsDragging(false), 50);

  const createdMoment   = getOrderDateMoment(order, 'created');
  const deliveryMoment  = getOrderDateMoment(order, 'delivery');
  const contragentLabel = order.contragent_name || 'Не указан';
  const deliveryAddress = order?.delivery_info?.address || '';
  const statusInfo      = fullStatusGroups[order.order_status] || { title: order.order_status || '—', color: 'default' };

  const renderAssignee = (a) => {
    if (!a) return 'Не назначен';
    if (Array.isArray(a)) return a.length > 0 ? renderAssignee(a[0]) : 'Не назначен';
    if (typeof a === 'object' && a.id && typeof a.id === 'object') return renderAssignee(a.id);
    if (typeof a === 'string' || typeof a === 'number') return String(a);
    const first  = (a.first_name ?? a.first ?? a.name ?? '').toString().trim();
    const second = (a.last_name  ?? a.surname ?? '').toString().trim();
    const full   = [first, second].filter(Boolean).join(' ');
    const disp   = full || a.full_name || a.display_name || (a.id ? `ID: ${a.id}` : 'Не назначен');
    return <span className="assignee-name" title={disp}>{disp}</span>;
  };

  const CdekBadge = (!isCdekMode && cdekInfo) ? (
    <Tooltip title={cdekInfo.text}>
      <Tag icon={<TruckOutlined />} color={cdekInfo.color}
        style={{ margin: '4px 0 0', fontSize: 11, lineHeight: '18px', padding: '0 6px' }}>
        {cdekInfo.text}
      </Tag>
    </Tooltip>
  ) : null;

  return (
    <Card
      className={`order-card ${isDragging ? 'dragging' : ''} ${isMobile ? 'mobile-card' : ''}`}
      draggable onDragStart={onDragStart} onDragEnd={onDragEnd}
      onClick={() => { if (!isDragging) onOpenDetails(order); }}
      onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && !isDragging) { e.preventDefault(); onOpenDetails(order); } }}
      role="button" tabIndex={0}
      bodyStyle={isMobile ? { padding: 8 } : { padding: 12 }}
    >
      {isMobile ? (
        <div className="mobile-card-content">
          <div className="mobile-card-header">
            <Tag color="blue" style={{ fontWeight: 700, margin: 0 }}>#{order.number}</Tag>
            <div className="mobile-header-right">
              {priorityMeta && <Tag color={priorityMeta.color} style={{ margin: 0, fontSize: 11 }}>{priorityMeta.label}</Tag>}
              {!isCdekMode && <Tag color={statusInfo.color} style={{ margin: 0, fontSize: 11 }}>{statusInfo.title}</Tag>}
            </div>
          </div>
          <div className="mobile-card-body">
            <div className="mobile-card-row"><UserOutlined className="mobile-icon" /><span>{contragentLabel}</span></div>
            <div className="mobile-card-row"><EnvironmentOutlined className="mobile-icon" /><span>{deliveryAddress || 'Не указано'}</span></div>
            <div className="mobile-card-row compact">
              <ClockCircleOutlined className="mobile-icon" />
              <span>{formatDate(deliveryMoment)}</span>
              <span>{renderFullPaid(order)}</span>
            </div>
            {CdekBadge}
          </div>
        </div>
      ) : (
        <>
          <div className="card-top">
            <div className="card-top-left">
              <Tag color="blue" style={{ fontWeight: 700, padding: '6px 10px', fontSize: 13 }}>#{order.number}</Tag>
            </div>
            <div className="card-top-right">
              {priorityMeta && <Tag color={priorityMeta.color} style={{ margin: 0 }}>{priorityMeta.label}</Tag>}
              {!isCdekMode && <Tag color={statusInfo.color} style={{ margin: 0 }}>{statusInfo.title}</Tag>}
            </div>
          </div>
          <div className="card-content">
            <div className="order-sum-compact">
              <div className="sum-value">{Number(order.sum || 0).toLocaleString('ru-RU')} ₽</div>
              <div className="sum-meta"><ShoppingCartOutlined /> {Number(order.nomenclature_count || 0)} шт.</div>
            </div>
            <div className="center-row">
              <div className="contragent">
                <UserOutlined className="ic" />
                <span className="contragent-name">{contragentLabel}</span>
                {Array.isArray(order.tags) && order.tags.length > 0 && <TagOutlined style={{ marginLeft: 8 }} />}
              </div>
            </div>
            <div className="center-row">
              <div className="meta-inline">
                <div title="Сборщик" className="meta-item"><IdcardOutlined /> {renderAssignee(order.assigned_picker)}</div>
                <div title="Курьер"  className="meta-item"><UserOutlined />   {renderAssignee(order.assigned_courier)}</div>
              </div>
            </div>
            <div className="center-row">
              <div className="address"><EnvironmentOutlined className="ic" /> {deliveryAddress || 'Не указано'}</div>
            </div>
            <div className="center-row small">
              <div className="time"><ClockCircleOutlined /> {formatDate(createdMoment)}</div>
              <div className="time"><CalendarOutlined />    {formatDate(deliveryMoment)}</div>
              <div className="paid-status">{renderFullPaid(order)}</div>
            </div>
            {CdekBadge && <div className="center-row" style={{ marginTop: 4 }}>{CdekBadge}</div>}
          </div>
        </>
      )}
    </Card>
  );
};

export default DocsSalesCalendar;